import loadable from "@loadable/component"
import { FocusScope } from "@react-aria/focus"
import { AnimatePresence } from "framer-motion"
import PropTypes from "prop-types"
import { useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import ScrollLock from "react-scrolllock-with-horizontal-scroll"

import { setExperiment } from "app-store-v2/app/actions"
import withHeader from "areas/header/hoc/with-header"
import MobileMenu from "areas/header/mobile-menu"
import Divider from "components/ui/divider"
import { jewlr_t, isTestMode, retry } from "helpers/application"
import useAriaHidden from "helpers/use-aria-hidden"
import { useNavigation } from "helpers/use-navigation"

import OverLay from "../overlay"

const LandingCollectionMenu = loadable(() =>
  retry(() => import("areas/landing/elements/landing-collection-menu"))
)

const CountrySwitcher = loadable(() =>
  retry(() => import("./country-switcher"))
)
const CustomerCare = loadable(() => retry(() => import("./customer-care")))
const InfoNav = loadable(() => retry(() => import("./info-nav")))
const MobileNav = loadable(() => retry(() => import("./mobile-nav")))
const MobileSubmenu = loadable(() => retry(() => import("./mobile-submenu")))

// Hardcoded collections quicklink element
const jewelryCollectionsElement = {
  anchor_name: "drawer-jewelry-collections",
  contain: true,
  data: {
    properties: {
      // Custom style properties for mobile drawer
      carouselContentProps: {
        className: "px-16 tablet:justify-center",
        style: {
          "--base-basis": "20vw",
          "--desktop-basis": "10vw",
          "--large-basis": "120px",
          "--tablet-basis": "15vw",
          "--xlarge-basis": "150px",
        },
        viewportClassName: "py-16",
      },
      carouselControlsProps: {
        className: "hidden",
      },
      carouselProps: {
        // className: "mobile-quicklinks-carousel",
      },
      disableSkip: true,
      itemTitleProps: {
        className: "text-12 px-4",
      },
    },
  },
  element_type: "collection-menu",
  items: [
    {
      image: {
        height: 280,
        src: "https://cms.jewlr.com/womens_rings_0e9a0a5f66/womens_rings_0e9a0a5f66.png",
        width: 280,
      },
      title: "Women's Rings",
      url: "/womens-rings",
    },
    {
      image: {
        height: 280,
        src: "https://cms.jewlr.com/womens_promise_rings_b1b3e20841/womens_promise_rings_b1b3e20841.png",
        width: 280,
      },
      title: "Women's Promise Rings",
      url: "/promise-rings-for-her",
    },
    {
      image: {
        height: 280,
        src: "https://cms.jewlr.com/womens_necklaces_8e4b10d598/womens_necklaces_8e4b10d598.png",
        width: 280,
      },
      title: "Women's Necklaces",
      url: "/womens-necklaces",
    },
    {
      image: {
        height: 280,
        src: "https://cms.jewlr.com/mens_promise_rings_303663c6c7/mens_promise_rings_303663c6c7.png",
        width: 280,
      },
      title: "Men's Promise Rings",
      url: "/mens-promise-rings",
    },
    {
      image: {
        height: 280,
        src: "https://cms.jewlr.com/engagement_rings_046deafc8a/engagement_rings_046deafc8a.png",
        width: 280,
      },
      title: "Engagement Rings",
      url: "/all-engagement-rings",
    },
    {
      image: {
        height: 280,
        src: "https://cms.jewlr.com/birthstone_jewelry_8248986f46/birthstone_jewelry_8248986f46.png",
        width: 280,
      },
      title: `Birthstone ${jewlr_t("terms.Jewelry")}`,
      url: `/birthstone-${jewlr_t("terms.jewelry")}`,
    },
    {
      image: {
        height: 280,
        src: "https://cms.jewlr.com/mothers_rings_89f87dbac0/mothers_rings_89f87dbac0.png",
        width: 280,
      },
      title: "Mother's Rings",
      url: "/mothers-rings",
    },
    {
      image: {
        height: 280,
        src: "https://cms.jewlr.com/name_necklaces_9d207605cf/name_necklaces_9d207605cf.png",
        width: 280,
      },
      title: "Name Necklaces",
      url: "/name-necklaces",
    },
  ],
}

const MobileMenuContent = ({ showMenu, toggleMenu }) => {
  const dispatch = useDispatch()
  const menuRef = useRef()
  const keyboardProps = useNavigation({
    onEscape: () => toggleMenu(showMenu),
  })

  // AB Test: Mobile Quick Nav
  const mobileQuickNavExp = useSelector(
    (state) => state.experiment.mobileQuickNav
  )

  // Trigger experiment impression
  useEffect(() => {
    if (mobileQuickNavExp) {
      dispatch(
        setExperiment({
          mobileQuickNav: mobileQuickNavExp,
        })
      )
    }
  }, [mobileQuickNavExp, dispatch])

  useAriaHidden({ ref: menuRef })

  return (
    <MobileMenu id="mobile-menu" ref={menuRef} {...keyboardProps}>
      <ScrollLock isActive={showMenu}>
        <div className="side-menu-content">
          {mobileQuickNavExp?.variant_id === 1 && (
            <LandingCollectionMenu
              element={jewelryCollectionsElement}
              handleItemClick={() => {
                try {
                  if (!isTestMode) {
                    dataLayer.push({
                      event: "clicked-mobile-drawer-quick-links",
                    })
                  }
                } catch (e) {
                  console.log(
                    "Error sending clicked-mobile-drawer-quick-links event",
                    e
                  )
                }
              }}
            />
          )}
          <nav className="px-12 py-10">
            <ul aria-label="Main Navigation" className="list-none" role="menu">
              <MobileNav />
              <Divider className="my-0" />
              {sl.config.global_e_country_switcher ? (
                <span data-ge-mobile-shippingswitcher-flag />
              ) : (
                <CountrySwitcher />
              )}
              <InfoNav />
            </ul>
          </nav>
          <Divider className="my-0" />
          <CustomerCare />
        </div>
        <MobileSubmenu />
      </ScrollLock>
    </MobileMenu>
  )
}

MobileMenuContent.propTypes = {
  showMenu: PropTypes.bool,
  toggleMenu: PropTypes.func,
}

const InternalMobileMenu = ({ showMenu, toggleMenu }) => (
  <AnimatePresence>
    {showMenu && (
      <>
        <FocusScope autoFocus contain restoreFocus>
          <MobileMenuContent showMenu={showMenu} toggleMenu={toggleMenu} />
        </FocusScope>
        <OverLay />
      </>
    )}
  </AnimatePresence>
)

InternalMobileMenu.propTypes = {
  showMenu: PropTypes.bool,
  toggleMenu: PropTypes.func,
}

export default withHeader(InternalMobileMenu)
